import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Sheets-free accounting ingest & annotation (docs/specs/accounting-ingest-and-annotation.md)

export interface AccountingStudent {
  id: number;
  firstName: string;
  lastName: string;
  surname?: string | null;
}

export interface AccountingTransaction {
  id: number;
  paymentDate: string;
  amount: string;
  currency: string;
  fullName: string;
  cardHolderName?: string | null;
  cardNumber?: string | null;
  studentId?: number | null;
  student?: AccountingStudent | null;
  type?: 'group' | 'individual' | null;
  lessonsCount?: number | null;
  qty?: number | null;
  unitPrice?: number | null;
  bankPaymentDocument?: string | null;
  bankPaymentDocumentDate?: string | null;
  annotatedAt?: string | null;
  status: 'paid' | 'returned';
  certificate?: AccountingCertificate | null;
}

// The act row (accept_certificates). Assigned on prod ahead of PDF rendering;
// generatedAt is stamped later by the local generator (null = PDF not yet rendered).
export interface AccountingCertificate {
  id: number;
  certNumber: number;
  generatedAt: string | null;
  servicePeriodTo: string | null;
}

export interface AssignActsResult {
  assigned: number;
  skippedExisting: number;
  skippedUnready: number;
  startNumber: number | null;
  lastNumber: number | null;
}

export interface RefundFlag {
  referenceNumber: string;
  transactionId: number;
  paymentDate: string | null;
  amount: string | null;
  fullName: string | null;
  status: 'paid' | 'returned';
}

export interface ParseError {
  row: number;
  reason: string;
}

export interface AcquiringUploadSummary {
  created: number;
  autoLinked: number;
  skippedDuplicates: number;
  skippedDeleted: number;
  skippedByStatus: Record<string, number>;
  refundFlags: RefundFlag[];
  parseErrors: ParseError[];
}

export interface OtherCredit {
  rowNumber: number;
  date: string;
  documentNumber: string;
  amount: string;
  description: string;
}

export interface StatementUploadSummary {
  created: number;
  skippedDuplicates: number;
  otherCredits: OtherCredit[];
  parseErrors: ParseError[];
}

export interface UnmatchedDoc {
  documentNumber: string;
  date: string;
  amount: string;
  reason: string;
  available?: number;
  neighborDocumentNumber?: string;
  neighborDate?: string;
  neighborAmount?: string;
  neighborReason?: string;
  neighborAvailable?: number;
}

export interface MatchStats {
  success: boolean;
  docsTotal: number;
  docsProcessed: number;
  docsMatched: number;
  txMatched: number;
  totalAssignedAmount: number;
  unmatchedDocs: UnmatchedDoc[];
  details: { documentNumber: string; date: string; matchedTxIds: number[]; amount: number }[];
}

export interface AccountingPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface AccountingFilters {
  quarter: string | null;
  annotated: 'all' | 'annotated' | 'unannotated';
  matched: 'all' | 'matched' | 'unmatched';
}

// КУДИР quarter tax totals (bucketed by act service-period end, matching the book).
export interface KudirSummary {
  scope: string;
  count: number;
  total: number;
  netIncome: number;
  tax: number;
}

// КУДИР revenue time-series for the Stats chart (one point per month/year bucket).
export interface KudirStatsPoint {
  bucket: string;      // 'YYYY-MM' or 'YYYY'
  revenue: number;
  netIncome: number;
  tax: number;
  count: number;
}
export interface KudirStats {
  axis: 'service' | 'payment';
  groupBy: 'month' | 'quarter' | 'year';
  series: KudirStatsPoint[];
}

interface AccountingState {
  transactions: AccountingTransaction[];
  pagination: AccountingPagination;
  filters: AccountingFilters;
  loading: boolean;
  error: string | null;
  acquiringSummary: AcquiringUploadSummary | null;
  statementSummary: StatementUploadSummary | null;
  uploadingAcquiring: boolean;
  uploadingStatement: boolean;
  matchStats: MatchStats | null;
  matching: boolean;
  savingIds: number[];
  bulkBusy: boolean;
  exporting: boolean;
  kudirSummary: KudirSummary | null;
  kudirStats: KudirStats | null;
  kudirStatsLoading: boolean;
}

const initialState: AccountingState = {
  transactions: [],
  pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 50 },
  filters: { quarter: null, annotated: 'all', matched: 'all' },
  loading: false,
  error: null,
  acquiringSummary: null,
  statementSummary: null,
  uploadingAcquiring: false,
  uploadingStatement: false,
  matchStats: null,
  matching: false,
  savingIds: [],
  bulkBusy: false,
  exporting: false,
  kudirSummary: null,
  kudirStats: null,
  kudirStatsLoading: false,
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchAccountingTransactions = createAsyncThunk(
  'accounting/fetchTransactions',
  async (
    params: { page?: number; limit?: number; quarter?: string | null; year?: number; annotated?: 'all' | 'annotated' | 'unannotated'; matched?: 'all' | 'matched' | 'unmatched' } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const { page = 1, limit = 50, quarter, year, annotated = 'all', matched = 'all' } = params;
      const queryParams = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (year !== undefined && year !== null) queryParams.append('year', String(year));
      else if (quarter) queryParams.append('quarter', quarter);
      if (annotated !== 'all') queryParams.append('annotated', annotated === 'annotated' ? 'true' : 'false');
      if (matched !== 'all') queryParams.append('matched', matched === 'matched' ? 'true' : 'false');

      const response = await axios.get(`${API_BASE_URL}/accounting/transactions?${queryParams}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch transactions'));
    }
  }
);

export const uploadAcquiringReport = createAsyncThunk(
  'accounting/uploadAcquiring',
  async (file: File, { rejectWithValue, getState }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_BASE_URL}/accounting/uploads/acquiring`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders(getState) }
      });
      return response.data.summary as AcquiringUploadSummary;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to upload acquiring report'));
    }
  }
);

export const uploadStatement = createAsyncThunk(
  'accounting/uploadStatement',
  async (file: File, { rejectWithValue, getState }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_BASE_URL}/accounting/uploads/statement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders(getState) }
      });
      return response.data.summary as StatementUploadSummary;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to upload statement'));
    }
  }
);

export const annotateTransaction = createAsyncThunk(
  'accounting/annotateTransaction',
  async (
    params: { id: number; studentId: number; type: 'group' | 'individual'; lessonsCount: number; unitPrice: number },
    { rejectWithValue, getState }
  ) => {
    try {
      const { id, ...body } = params;
      const response = await axios.patch(`${API_BASE_URL}/accounting/transactions/${id}/annotation`, body, {
        headers: getAuthHeaders(getState)
      });
      return response.data.transaction as AccountingTransaction;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to save annotation'));
    }
  }
);

export const setTransactionStatus = createAsyncThunk(
  'accounting/setTransactionStatus',
  async (params: { id: number; status: 'paid' | 'returned' }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/accounting/transactions/${params.id}/status`, { status: params.status }, {
        headers: getAuthHeaders(getState)
      });
      return response.data.transaction as AccountingTransaction;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update status'));
    }
  }
);

export const runPaymentDocMatching = createAsyncThunk(
  'accounting/runPaymentDocMatching',
  async (_: void, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/accounting/match-payment-docs`, {}, {
        headers: getAuthHeaders(getState)
      });
      return response.data.stats as MatchStats;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Payment doc matching failed'));
    }
  }
);

export interface BulkFilters {
  quarter?: string | null;
  matched?: 'all' | 'matched' | 'unmatched';
}

function filtersToParams(filters: BulkFilters) {
  const body: Record<string, string> = {};
  if (filters.quarter) body.quarter = filters.quarter;
  if (filters.matched && filters.matched !== 'all') body.matched = filters.matched === 'matched' ? 'true' : 'false';
  return body;
}

export const applyGroupDefaults = createAsyncThunk(
  'accounting/applyGroupDefaults',
  async (filters: BulkFilters, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/accounting/apply-group-defaults`, filtersToParams(filters), {
        headers: getAuthHeaders(getState)
      });
      return response.data as { filled: number; skippedIrregular: number };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to apply group defaults'));
    }
  }
);

// Downloads the filtered set (quarter, or a whole year via { year }) as a CSV file.
export const exportTransactionsCsv = createAsyncThunk(
  'accounting/exportCsv',
  async (scope: { quarter?: string | null; year?: number; matched?: 'all' | 'matched' | 'unmatched' }, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      if (scope.year) params.append('year', String(scope.year));
      else if (scope.quarter) params.append('quarter', scope.quarter);
      if (scope.matched && scope.matched !== 'all') params.append('matched', scope.matched === 'matched' ? 'true' : 'false');

      const response = await axios.get(`${API_BASE_URL}/accounting/export?${params}`, {
        headers: getAuthHeaders(getState),
        responseType: 'blob'
      });
      const filename = scope.year ? `accounting-${scope.year}.csv` : `accounting-${scope.quarter || 'all'}.csv`;
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return filename;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to export CSV'));
    }
  }
);

// Assigns act (certificate) numbers to the confirmed, act-less rows in the current
// filter. startNumber anchors the first prod run (10681); later runs auto-continue.
export const assignActNumbers = createAsyncThunk(
  'accounting/assignActNumbers',
  async (params: BulkFilters & { startNumber?: number | null }, { rejectWithValue, getState }) => {
    try {
      const body: Record<string, string> = filtersToParams(params);
      if (params.startNumber != null) body.startNumber = String(params.startNumber);
      const response = await axios.post(`${API_BASE_URL}/accounting/assign-act-numbers`, body, {
        headers: getAuthHeaders(getState)
      });
      return response.data as AssignActsResult;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to assign act numbers'));
    }
  }
);

// Downloads the КУДИР tax book (act-driven, bucketed by service-period end) as a CSV.
export const exportKudirCsv = createAsyncThunk(
  'accounting/exportKudir',
  async (scope: { quarter?: string | null; year?: number }, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      if (scope.year) params.append('year', String(scope.year));
      else if (scope.quarter) params.append('quarter', scope.quarter);

      const response = await axios.get(`${API_BASE_URL}/accounting/kudir?${params}`, {
        headers: getAuthHeaders(getState),
        responseType: 'blob'
      });
      const filename = scope.year ? `kudir-${scope.year}.xlsx` : `kudir-${scope.quarter || 'all'}.xlsx`;
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return filename;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to export КУДИР'));
    }
  }
);

// КУДИР quarter tax totals for the header (act-driven, bucketed by service-period end).
export const fetchKudirSummary = createAsyncThunk(
  'accounting/fetchKudirSummary',
  async (scope: { quarter?: string | null; year?: number }, { rejectWithValue, getState }) => {
    try {
      const params = new URLSearchParams();
      if (scope.year) params.append('year', String(scope.year));
      else if (scope.quarter) params.append('quarter', scope.quarter);

      const response = await axios.get(`${API_BASE_URL}/accounting/kudir-summary?${params}`, {
        headers: getAuthHeaders(getState),
      });
      return response.data as KudirSummary;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load КУДИР summary'));
    }
  }
);

// КУДИР revenue time-series for the Stats chart (bucketed per month/year, on the service or payment axis).
export const fetchKudirStats = createAsyncThunk(
  'accounting/fetchKudirStats',
  async (params: { axis: 'service' | 'payment'; groupBy: 'month' | 'quarter' | 'year' }, { rejectWithValue, getState }) => {
    try {
      const query = new URLSearchParams({ axis: params.axis, groupBy: params.groupBy });
      const response = await axios.get(`${API_BASE_URL}/accounting/kudir-stats?${query}`, {
        headers: getAuthHeaders(getState),
      });
      return response.data as KudirStats;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load КУДИР stats'));
    }
  }
);

const replaceTransaction = (state: AccountingState, transaction: AccountingTransaction) => {
  const index = state.transactions.findIndex((t) => t.id === transaction.id);
  if (index !== -1) state.transactions[index] = transaction;
};

const accountingSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AccountingFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSummaries: (state) => {
      state.acquiringSummary = null;
      state.statementSummary = null;
      state.matchStats = null;
    },
    clearKudirSummary: (state) => {
      state.kudirSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountingTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountingTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAccountingTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadAcquiringReport.pending, (state) => {
        state.uploadingAcquiring = true;
        state.error = null;
      })
      .addCase(uploadAcquiringReport.fulfilled, (state, action) => {
        state.uploadingAcquiring = false;
        state.acquiringSummary = action.payload;
      })
      .addCase(uploadAcquiringReport.rejected, (state, action) => {
        state.uploadingAcquiring = false;
        state.error = action.payload as string;
      })
      .addCase(uploadStatement.pending, (state) => {
        state.uploadingStatement = true;
        state.error = null;
      })
      .addCase(uploadStatement.fulfilled, (state, action) => {
        state.uploadingStatement = false;
        state.statementSummary = action.payload;
      })
      .addCase(uploadStatement.rejected, (state, action) => {
        state.uploadingStatement = false;
        state.error = action.payload as string;
      })
      .addCase(annotateTransaction.pending, (state, action) => {
        state.savingIds.push(action.meta.arg.id);
        state.error = null;
      })
      .addCase(annotateTransaction.fulfilled, (state, action) => {
        state.savingIds = state.savingIds.filter((id) => id !== action.meta.arg.id);
        replaceTransaction(state, action.payload);
      })
      .addCase(annotateTransaction.rejected, (state, action) => {
        state.savingIds = state.savingIds.filter((id) => id !== action.meta.arg.id);
        state.error = action.payload as string;
      })
      .addCase(setTransactionStatus.pending, (state, action) => {
        state.savingIds.push(action.meta.arg.id);
        state.error = null;
      })
      .addCase(setTransactionStatus.fulfilled, (state, action) => {
        state.savingIds = state.savingIds.filter((id) => id !== action.meta.arg.id);
        replaceTransaction(state, action.payload);
      })
      .addCase(setTransactionStatus.rejected, (state, action) => {
        state.savingIds = state.savingIds.filter((id) => id !== action.meta.arg.id);
        state.error = action.payload as string;
      })
      .addCase(runPaymentDocMatching.pending, (state) => {
        state.matching = true;
        state.error = null;
      })
      .addCase(runPaymentDocMatching.fulfilled, (state, action) => {
        state.matching = false;
        state.matchStats = action.payload;
      })
      .addCase(runPaymentDocMatching.rejected, (state, action) => {
        state.matching = false;
        state.error = action.payload as string;
      })
      .addCase(applyGroupDefaults.pending, (state) => { state.bulkBusy = true; state.error = null; })
      .addCase(applyGroupDefaults.fulfilled, (state) => { state.bulkBusy = false; })
      .addCase(applyGroupDefaults.rejected, (state, action) => { state.bulkBusy = false; state.error = action.payload as string; })
      .addCase(exportTransactionsCsv.pending, (state) => { state.exporting = true; state.error = null; })
      .addCase(exportTransactionsCsv.fulfilled, (state) => { state.exporting = false; })
      .addCase(exportTransactionsCsv.rejected, (state, action) => { state.exporting = false; state.error = action.payload as string; })
      .addCase(assignActNumbers.pending, (state) => { state.bulkBusy = true; state.error = null; })
      .addCase(assignActNumbers.fulfilled, (state) => { state.bulkBusy = false; })
      .addCase(assignActNumbers.rejected, (state, action) => { state.bulkBusy = false; state.error = action.payload as string; })
      .addCase(exportKudirCsv.pending, (state) => { state.exporting = true; state.error = null; })
      .addCase(exportKudirCsv.fulfilled, (state) => { state.exporting = false; })
      .addCase(exportKudirCsv.rejected, (state, action) => { state.exporting = false; state.error = action.payload as string; })
      .addCase(fetchKudirSummary.fulfilled, (state, action) => { state.kudirSummary = action.payload; })
      .addCase(fetchKudirSummary.rejected, (state) => { state.kudirSummary = null; })
      .addCase(fetchKudirStats.pending, (state) => { state.kudirStatsLoading = true; state.error = null; })
      .addCase(fetchKudirStats.fulfilled, (state, action) => { state.kudirStatsLoading = false; state.kudirStats = action.payload; })
      .addCase(fetchKudirStats.rejected, (state, action) => { state.kudirStatsLoading = false; state.error = action.payload as string; });
  },
});

export const { setFilters, setPage, clearError, clearSummaries, clearKudirSummary } = accountingSlice.actions;
export default accountingSlice.reducer;
